import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { CampaignsContext } from "contexts/Campaigns";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { GamificationContext } from "contexts/Gamification";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Campaign,
  CampaignStatus,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import { EMAIL_TARGET_FOR_FREE } from "constants/influencer";
import { OrganizationGamificationTask } from "features/Influencer/HomePage/Gamification/schema";
import { fetcherAuth } from "utils/api";
import { getParentRoute, isPage } from "utils/general";
import { trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";
import { useSubscription } from "utils/useSubscription";

import CampaignMethodChoice from "./CampaignMethodChoice";
import StepperHeader from "./SharedComponents/StepperHeader";
import CampaignBrandsV2 from "./Steps/CampaignBrands";
import CampaignDetailsV2 from "./Steps/CampaignDetails";
import CampaignReview from "./Steps/CampaignReview";
import CampaignTemplates from "./Steps/CampaignTemplates";
import { CampaignStep, CampaignStepOrders, getActiveStep } from "./schema";

export default function NewCampaign() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { campaign, setCampaign } = useContext(NewCampaignContext);
  const { topLevelCategories, fetchBentoCategories } = useContext(
    BentoCategoriesContext,
  );
  const { setCampaigns } = useContext(CampaignsContext);
  const { openAssistant } = useContext(DiscoverViewContext);
  const { campaignId } = useParams();
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const { decreaseEmailCount } = useSubscription();
  const { completeTask, refreshGamificationProfile } = useGamification();
  const { pitchCount, setPitchCount } = useContext(GamificationContext);
  const [fetchingCampaign, setFetchingCampaign] = useState(!!campaignId);
  const [confirmSkipOpen, setConfirmSkipOpen] = useState(false);
  const [methodChosen, setMethodChosen] = useState(false);

  const handleClose = (exitManually: boolean) => {
    if (isPage(routes.onboardingPro)) {
      if (exitManually) {
        setConfirmSkipOpen(true);
        return;
      }
      navigate(`/${routes.onboardingProComplete}${location.search}`);
      return;
    }
    if (isPage(routes.home)) {
      // User just create a campaign, refresh to update their email streaks if applicable.
      if (!exitManually) refreshGamificationProfile(true);
      navigate(`/${routes.home}`);
      return;
    }
    navigate(`/${routes.campaignView}`);
  };

  const handleUseAI = () => {
    openAssistant({
      promptPage: "campaigns",
      source: "New Campaign Dialog",
      initialMessage: "Help me set up a new campaign with AI.",
    });
    handleClose(false);
  };

  const handleConfirmSkip = () => {
    trackEvent("Onboarding - Campaign Creation Skipped");
    setConfirmSkipOpen(false);
    navigate(`/${routes.onboardingProComplete}${location.search}`);
  };

  const handleKeepCreating = () => {
    trackEvent("Onboarding - Campaign Keep Creating");
    setConfirmSkipOpen(false);
  };

  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const activeStep = getActiveStep(location.pathname);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchCampaign = async () => {
    if (!campaignId) {
      redirect(CampaignStep.details);
      return;
    }
    if (!currentOrg) return;
    setFetchingCampaign(true);
    try {
      const { campaign, brandsCount, campaignBrands } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${campaignId}?per_page=10`,
        "GET",
      );
      setCampaign(campaign);
      if (brandsCount !== undefined) {
        setCampaign((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            campaignBrandCount: brandsCount,
          };
        });
      }
      if (campaignBrands !== undefined) {
        setCampaign((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            campaignBrands,
          };
        });
      }
      const stepInRoute = CampaignStepOrders.find((step) =>
        location.pathname.includes(step),
      );

      if (campaign?.status !== CampaignStatus.draft) {
        redirect(CampaignStep.review, campaign.id);
        return;
      }
      if (stepInRoute) {
        redirect(stepInRoute as CampaignStep, campaign?.id);
      } else {
        const lastActiveStep = getLastActiveStep(campaign, brandsCount);
        redirect(lastActiveStep || CampaignStep.details, campaign?.id);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchingCampaign(false);
    }
  };

  const getLastActiveStep = (campaign: Campaign, brandsCount: number) => {
    if (!(campaign?.name && campaign?.startDate)) {
      return CampaignStep.details;
    }
    if (!(brandsCount && brandsCount > 0)) {
      return CampaignStep.select_brands;
    }
    if (
      !(
        campaign?.emailSetting?.emailSettingTemplates &&
        campaign?.emailSetting?.emailSettingTemplates?.length > 0
      )
    ) {
      return CampaignStep.write_email;
    }
    return CampaignStep.review;
  };

  const handleUpdateCampaign = async (body: Map, skipMoveNext?: boolean) => {
    try {
      setSubmitLoading(true);
      const { campaign: updatedCampaign } = await fetcherAuth(
        getToken,
        campaign?.id
          ? `/api/organization/${currentOrg?.id}/campaigns/${campaign?.id}/draft`
          : `/api/organization/${currentOrg?.id}/campaigns/draft`,
        campaign?.id ? "PATCH" : "POST",
        {},
        body,
      );
      const isLaunching =
        body.status === CampaignStatus.in_progress &&
        campaign?.status !== CampaignStatus.in_progress;
      if (isLaunching) {
        const newBrandCount = updatedCampaign?.campaignBrandCount ?? 0;
        decreaseEmailCount(newBrandCount);

        // Optimistically advance the free-user pitch progress so the gamification
        // card on the home page
        const newPitchCount = (pitchCount ?? 0) + newBrandCount;
        setPitchCount(newPitchCount);
        if (newPitchCount >= EMAIL_TARGET_FOR_FREE) {
          completeTask(OrganizationGamificationTask.send_pitch);
        }
        const hasFollowupTemplate =
          (updatedCampaign?.emailSetting?.emailSettingTemplates?.length ?? 0) >
          1;
        if (hasFollowupTemplate) {
          completeTask(OrganizationGamificationTask.send_followup);
        }
      }
      setCampaign(updatedCampaign);
      setCampaigns((prev) => {
        if (!prev) return [updatedCampaign];
        const existingIndex = prev.findIndex(
          (c) => c.id === updatedCampaign.id,
        );
        if (existingIndex !== -1) {
          const newCampaigns = [...prev];
          newCampaigns[existingIndex] = updatedCampaign;
          return newCampaigns;
        }
        return [...prev, updatedCampaign];
      });
      if (errorMessage) setErrorMessage("");
      if (!skipMoveNext) moveNext(updatedCampaign?.id);
    } catch (error) {
      if (error?.message) {
        setErrorMessage(error.message);
      }
      setErrorAlert(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const moveNext = (campaignId?: number) => {
    if (isPage(CampaignStep.review)) {
      handleClose(false);
      return;
    }
    const currentStepIndex = getActiveStep(location.pathname);
    let stepName = "";
    if (currentStepIndex < CampaignStepOrders.length - 1) {
      stepName = CampaignStepOrders[currentStepIndex + 1];
    }
    if (stepName) {
      redirect(stepName as CampaignStep, campaignId || campaign?.id);
    }
  };

  const moveBack = () => {
    if (
      campaign?.status !== CampaignStatus.draft &&
      campaign?.status !== CampaignStatus.pending_approval
    ) {
      handleClose(false);
      return;
    }
    const currentStepIndex = getActiveStep(location.pathname);
    let stepName = "";
    if (currentStepIndex > 0) {
      stepName = CampaignStepOrders[currentStepIndex - 1];
    }
    if (stepName) {
      redirect(stepName as CampaignStep, campaign?.id);
    }
  };

  const redirect = (step: CampaignStep, campaignId?: number) => {
    const currentPathName = getParentRoute(location.pathname);
    if (campaignId) {
      navigate(`/${currentPathName}/campaign/${campaignId}/${step}`);
    } else {
      navigate(`/${currentPathName}/new/${step}`);
    }
  };

  useEffect(() => {
    if (topLevelCategories.length === 0) {
      fetchBentoCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchCampaign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, currentOrg?.id]);

  const noMessagesRemaining =
    subscription?.remainingMessages !== undefined &&
    subscription.remainingMessages <= 0 &&
    (!campaign?.status || campaign?.status === CampaignStatus.draft);

  useEffect(() => {
    if (noMessagesRemaining) {
      setUpgradeDialogSource(UpgradeDialogSource.campaigns_limit);
      handleClose(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noMessagesRemaining]);

  if (noMessagesRemaining) {
    return null;
  }

  if (fetchingCampaign) {
    return (
      <Dialog
        fullWidth
        onClose={() => handleClose(true)}
        maxWidth="lg"
        fullScreen={isMobileScreen}
        open={true}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 12,
          }}
        >
          <CircularProgress size={36} />
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Loading campaign…
          </Typography>
        </Box>
      </Dialog>
    );
  }

  const showMethodChoice =
    !campaignId &&
    !campaign?.id &&
    !methodChosen &&
    !isPage(routes.onboardingPro);

  if (showMethodChoice) {
    return (
      <Dialog
        fullWidth
        onClose={() => handleClose(true)}
        maxWidth="lg"
        fullScreen={isMobileScreen}
        open={true}
      >
        <CampaignMethodChoice
          onClose={() => handleClose(true)}
          onManual={() => setMethodChosen(true)}
          onUseAI={handleUseAI}
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      fullWidth
      onClose={() => handleClose(true)}
      maxWidth="lg"
      fullScreen={isMobileScreen}
      title="Create A New Campaign"
      open={true}
    >
      <Dialog
        open={confirmSkipOpen}
        onClose={handleKeepCreating}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
          Skip this step?
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.7 }}
          >
            No worries — you can create a campaign anytime from your dashboard
            after onboarding. Any details you've entered will be saved as a
            draft.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleKeepCreating}
            sx={{ textTransform: "none" }}
          >
            Keep creating
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSkip}
            sx={{ textTransform: "none" }}
          >
            Skip
          </Button>
        </DialogActions>
      </Dialog>
      <StepperHeader
        activeStep={activeStep}
        handleClose={() => handleClose(true)}
        campaignName={campaign?.name}
        onRename={
          campaign?.id
            ? async (name) => {
                await handleUpdateCampaign({ name }, true);
              }
            : undefined
        }
      />
      <Divider />

      <Routes>
        <Route
          path={CampaignStep.details}
          element={
            <CampaignDetailsV2
              handleClose={() => handleClose(true)}
              handleUpdateCampaign={handleUpdateCampaign}
              submitLoading={submitLoading}
            />
          }
        />
        <Route
          path={`${CampaignStep.select_brands}/*`}
          element={
            <CampaignBrandsV2
              handleUpdateCampaign={handleUpdateCampaign}
              moveBack={moveBack}
              moveNext={moveNext}
              submitLoading={submitLoading}
              errorMessage={errorMessage}
            />
          }
        />
        <Route
          path={`${CampaignStep.write_email}/*`}
          element={
            <CampaignTemplates
              handleUpdateCampaign={handleUpdateCampaign}
              moveBack={moveBack}
              submitLoading={submitLoading}
              errorMessage={errorMessage}
            />
          }
        />
        <Route
          path={`${CampaignStep.review}/*`}
          element={
            <CampaignReview
              handleUpdateCampaign={handleUpdateCampaign}
              moveBack={moveBack}
              submitLoading={submitLoading}
              errorMessage={errorMessage}
            />
          }
        />
      </Routes>
    </Dialog>
  );
}
